import { useEffect, useRef } from "react";
import { useSocket } from "socket.io-react-hook";
import { AUTH_DISABLED } from "src/App/Providers";
import { _getToken } from "src/Config/axiosApi";

/**
 * Hook for connecting to the orderbook-aggregator WebSocket
 * for crypto details (all orderbooks for a specific crypto across exchanges)
 */
export const useCryptoDetailsSocket = () => {
  const accessToken = _getToken();

  // Connect to orderbook-aggregator (dedicated service, no namespace needed)
  const wsUrl = import.meta.env.VITE_ORDERBOOK_AGGREGATOR_URL!;

  const { socket } = useSocket(wsUrl, {
    enabled: AUTH_DISABLED || !!accessToken,
    auth: (cb: (data: { token: string }) => void) =>
      cb({ token: _getToken() }),
  });

  const connectionStateRef = useRef({
    hasConnected: false,
  });

  useEffect(() => {
    if (!socket.connected) {
      connectionStateRef.current.hasConnected = false;
    }
  }, [socket.connected]);

  useEffect(() => {
    if (socket.connected && !connectionStateRef.current.hasConnected) {
      console.log("[CryptoDetails WS] Connected to orderbook-aggregator");
      connectionStateRef.current.hasConnected = true;
    }
  }, [socket.connected]);

  // Handle connection events
  useEffect(() => {
    const handleConnect = () => {
      console.log("[CryptoDetails WS] Socket connected");
    };

    const handleDisconnect = (reason: string) => {
      console.log("[CryptoDetails WS] Socket disconnected:", reason);
      connectionStateRef.current.hasConnected = false;
    };

    const handleError = (error: Error) => {
      console.error("[CryptoDetails WS] Socket error:", error);
    };

    socket.on("connect", handleConnect);
    socket.on("disconnect", handleDisconnect);
    socket.on("connect_error", handleError);

    return () => {
      socket.off("connect", handleConnect);
      socket.off("disconnect", handleDisconnect);
      socket.off("connect_error", handleError);
    };
  }, [socket]);

  return {
    socket,
    isConnected: socket.connected,
  };
};
